import React from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, Plus, Users, X } from "lucide-react";
import { ReactComponent as InfluencedIcon } from "assets/icon-influenced.svg";
import { ReactComponent as CrosspostIcon } from "assets/button-crosspost.svg";
import { formatDate, formatRelativeTime } from "config/dayjsConfig";
import TooltipWrapper from "ui/TooltipWrapper";
import AvatarRing from "ui/AvatarRing";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import type { Action, Issue, WikiSuggestion } from "util/constants";
import type { AvatarViewModel } from "./entityAvatar.types";
import type { EntityAvatarColorKey } from "./entityAvatar.types";
import { entityAvatarColorMap, entityAvatarSizeClass } from "./entityAvatarTokens";
import type { EntityAvatarEntityType } from "./entityAvatar.types";

export interface EntityAvatarPresentationProps {
  details: AvatarViewModel;
  entityType: EntityAvatarEntityType;
  entity?: Record<string, unknown> | Issue | WikiSuggestion | Action;
  className: string;
  ringClass: string;
  displayMode: "default" | "avatarOnly";
  isEntityProfilePage: boolean;
  isEntitySection: boolean;
  entityChip: boolean;
  showLink: boolean;
  compactCard: boolean;
  badgeText?: string;
  badgeElement?: React.ReactNode;
  showCloseChip: boolean;
  onEntityChipClose?: () => void;
  crosspostData?: { sourceType: string; targetType: string };
  isGeneralEntity: boolean;
  actorStoryHref?: string | null;
  hasActorStory?: boolean;
  /** True when the actor has at least one unseen story (drives ring color). */
  hasActorUnseenStory?: boolean;
  actorStoryLabel?: string;
}

type UserDetails = { isBestea?: boolean } | undefined;

/**
 * Presentational layouts for EntityAvatar (no data fetching).
 */
export function EntityAvatarPresentation(props: EntityAvatarPresentationProps): React.ReactElement {
  const {
    details,
    entityType,
    entity,
    className,
    ringClass,
    displayMode,
    isEntityProfilePage,
    isEntitySection,
    entityChip,
    showLink,
    compactCard,
    badgeText,
    badgeElement,
    showCloseChip,
    onEntityChipClose,
    crosspostData,
    isGeneralEntity,
    actorStoryHref,
    hasActorStory = false,
    hasActorUnseenStory = false,
    actorStoryLabel,
  } = props;

  const {
    title,
    subtitle,
    thumbnail,
    link,
    subLink,
    username,
    userAvatar,
    separator,
    parentEntityType,
    parentEntityColor,
    customChip,
  } = details;

  const userEntityDetails = details.userEntityDetails as UserDetails;

  const colorKey = parentEntityColor as EntityAvatarColorKey;
  /**
   * Some entity types don't have an attached issue/topic/title that makes
   * sense to headline — they are fundamentally "by this user" posts (feedback,
   * collection). For those, the avatar presentation collapses the title row
   * and the parent-type chip, mirroring the feedback treatment so we don't
   * double-surface the same username/title.
   */
  const isUserCenteredAvatar =
    entityType === "feedback" || entityType === "collection";
  const actorProfileHref =
    typeof username === "string" && username.trim().length > 0 ? `/user/${username}` : null;
  const actorAvatarHref = hasActorStory && actorStoryHref ? actorStoryHref : actorProfileHref;
  const shouldShowActorAvatar =
    Boolean(actorAvatarHref) && Boolean(username) && !isUserCenteredAvatar;
  /*
   * Actor mini-avatar inside an entity row. Renders the standardized
   * `<AvatarRing>` (story ring + image + initials fallback) and keeps
   * the existing parent `<Link>` for navigation, since the destination
   * already encodes the story-or-profile decision via `actorAvatarHref`.
   * The ring color reflects unseen state when known, falling back to
   * the muted "seen" ring if we only know a story exists.
   */
  const actorAvatarNode = shouldShowActorAvatar ? (
    <Link
      to={actorAvatarHref || "/"}
      onClick={(event) => event.stopPropagation()}
      aria-label={hasActorStory ? actorStoryLabel : username}
      className="flex-shrink-0"
    >
      <AvatarRing
        size={ACTOR_AVATAR_SIZES.mini}
        className={entityAvatarSizeClass.mini}
        ringState={
          !hasActorStory ? "none" : hasActorUnseenStory ? "unseen" : "seen"
        }
        imageUrl={userAvatar ?? null}
        imageAlt={username ?? ""}
        initials={(username?.charAt(0) || "?").toUpperCase()}
      />
    </Link>
  ) : null;

  const renderDateTooltip = (className: string): React.ReactElement => {
    const createdAt = (entity?.created_at as string | undefined) || "";
    return (
      <TooltipWrapper title={formatDate(createdAt)} arrow>
        <span className={className}>・{formatRelativeTime(createdAt)}</span>
      </TooltipWrapper>
    );
  };

  if (displayMode === "avatarOnly") {
    return (
      <div className={`entity-avatar-only flex items-center justify-center ${className}`}>
        <div
          className={`${entityAvatarSizeClass.standalone} rounded-full flex-shrink-0 overflow-hidden bg-transparent dark:bg-transparent ${ringClass} ${className}`}
        >
          {thumbnail ? (
            <img alt={title} src={thumbnail} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-text">
              {title?.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isEntityProfilePage) {
    return (
      <div
        className={`entity-profile-row flex min-w-0 items-center gap-1 pr-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}
      >
        <Link to={link} onClick={(e) => e.stopPropagation()}>
          <div
            className={`${entityAvatarSizeClass.thumb} flex-shrink-0 overflow-hidden rounded-full bg-transparent dark:bg-transparent ${ringClass}`}
          >
            {thumbnail ? (
              <img alt={title} src={thumbnail} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-text">
                {title.charAt(0).toUpperCase()}
                {subtitle && (
                  <>
                    {separator}
                    {subtitle?.charAt(0).toUpperCase()}
                  </>
                )}
              </div>
            )}
          </div>
        </Link>
        <div className="flex min-w-0 flex-col">
          <div className="group flex min-w-0 flex-row items-center gap-1">
            <span className="line-clamp-1 min-w-0 break-all font-semibold">
              {isUserCenteredAvatar ? (
                <div className="card-username flex flex-row items-center group line-clamp-1 max-w-[calc(100vw-80px)] min-w-0">
                  <TooltipWrapper title={`View ${username}'s profile`} arrow>
                    <Link to={`/user/${username}`} className="card-username line-clamp-1 break-all">
                      <span className="entity-avatar-overhang-safe line-clamp-1 break-all">{username}</span>
                    </Link>
                  </TooltipWrapper>
                  {renderDateTooltip("card-date flex flex-shrink-0 flex-row items-center gap-0 text-sm font-medium")}
                </div>
              ) : (
                <Link
                  to={link}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="group-hover:underline"
                >
                  {title}
                </Link>
              )}
              {subtitle && (
                <>
                  {separator
                    ? typeof separator === "string" ? (
                        <Link
                          to={link}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="group-hover:underline"
                        >
                          {separator}
                        </Link>
                      ) : (
                        separator
                      )
                    : " "}
                  <Link
                    to={subLink || link}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="group-hover:underline"
                  >
                    {subtitle}
                  </Link>
                </>
              )}
              {customChip && (
                <span className={`ml-2 rounded-full px-2 py-1 text-xs font-medium text-white ${customChip.color}`}>
                  {customChip.text}
                </span>
              )}
            </span>
            <span
              className={`flex-shrink-0 whitespace-nowrap rounded-full px-1.5 text-xs font-medium text-white hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""} ${
                isUserCenteredAvatar ? "hidden" : ""
              }`}
            >
              {parentEntityType}
            </span>
          </div>
          {username && !isUserCenteredAvatar && (
            <div className="card-username flex flex-row items-center group line-clamp-1 max-w-[calc(100vw-80px)] min-w-0">
              {actorAvatarNode ? <span className="mr-1 flex-shrink-0">{actorAvatarNode}</span> : null}
              {username && isGeneralEntity ? (
                <TooltipWrapper title={`View this ${entityType}`} arrow>
                  <Link
                    to={`/${entityType}/${entity?.id as number}`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="card-username line-clamp-1 break-all"
                  >
                    <span className="entity-avatar-overhang-safe text-sm font-medium line-clamp-1 break-all">{username}</span>
                  </Link>
                </TooltipWrapper>
              ) : (
                <TooltipWrapper title={`View ${username}'s profile`} arrow>
                  <Link
                    to={`/user/${username}`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="card-username line-clamp-1 break-all"
                  >
                    <span className="entity-avatar-overhang-safe text-sm font-medium line-clamp-1 break-all">{username}</span>
                  </Link>
                </TooltipWrapper>
              )}
              {renderDateTooltip("card-date flex flex-shrink-0 flex-row items-center gap-0 text-sm font-medium")}
              {badgeText && (
                <span
                  className={`flex flex-shrink-0 whitespace-nowrap rounded-full px-1.5 text-xs font-normal text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[entityType as EntityAvatarColorKey] ?? ""}`}
                >
                  {badgeText}
                </span>
              )}
              {badgeElement && <div className="flex-shrink-0 text-sm leading-tight">{badgeElement}</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isEntitySection) {
    return (
      <div className="flex flex-row items-center">
        <TooltipWrapper title={`View ${username}'s profile`} arrow>
          <Link
            to={`/user/${username}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div
              className={`group flex flex-row items-center text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}
            >
              <div
                className={`${entityAvatarSizeClass.actorRow} flex-shrink-0 overflow-hidden rounded-full bg-transparent dark:bg-transparent ${ringClass}`}
              >
                {userAvatar ? (
                  <img alt={username} src={userAvatar} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-text">
                    {username?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              {username && (
                <div className="card-username line-clamp-1 items-center rounded-full break-all group">
                  <span
                    className={`card-username line-clamp-1 break-all font-medium ${
                      isUserCenteredAvatar ? "font-semibold" : "text-sm"
                    }`}
                  >
                    {username}
                  </span>
                </div>
              )}
            </div>
          </Link>
        </TooltipWrapper>
        {renderDateTooltip("card-date flex flex-row items-center gap-0 text-sm font-medium")}
      </div>
    );
  }

  if (entityChip && !showLink && !compactCard) {
    return (
      <div
        className={`entity-chip-nolink group flex w-full min-w-0 flex-row items-center gap-1 rounded-full py-1 pl-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}
      >
        <div className={`${entityAvatarSizeClass.thumb} flex-shrink-0 overflow-hidden rounded-full bg-transparent dark:bg-transparent ${ringClass}`}>
          {thumbnail ? (
            <img alt={title} src={thumbnail} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-gray">
              {title.charAt(0).toUpperCase()}
              {subtitle && (
                <>
                  {separator}
                  {subtitle?.charAt(0).toUpperCase()}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-col justify-start gap-x-1">
          <div className="group flex min-w-0 flex-row items-center justify-between">
            <span className="flex min-w-0 flex-row items-center gap-1 break-all text-sm font-semibold line-clamp-1">
              {entityType === "team" && (
                <span className="flex-shrink-0">
                  <Users className={`size-3.5 ${userEntityDetails ? "text-green-600" : ""}`} />
                </span>
              )}
              {userEntityDetails?.isBestea && (
                <span className="flex-shrink-0">
                  <HeartHandshake className="size-3.5 text-green-600" />
                </span>
              )}
              <span className="line-clamp-1 break-all text-left text-sm leading-tight">{title}</span>
              {badgeText && (
                <span
                  className={`flex flex-shrink-0 whitespace-nowrap rounded-full px-1 text-xs text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""}`}
                >
                  {badgeText}
                </span>
              )}
              {badgeElement && <div className="flex-shrink-0 text-sm leading-tight">{badgeElement}</div>}
            </span>
          </div>
          {subtitle && (
            <>
              {entityType === "issue" && (entity as unknown as Issue)?.properties?.type === "Compound" ? (
                <div className="flex min-w-0 flex-shrink flex-row items-center gap-x-0.5">
                  <InfluencedIcon className="size-3.5 flex-shrink-0 rotate-180" />{" "}
                  <span className="line-clamp-1 flex-shrink break-all text-sm font-semibold">{subtitle}</span>
                </div>
              ) : (
                <div className="flex min-w-0 flex-shrink flex-row items-center gap-x-1">
                  <span className="line-clamp-1 break-all text-xs">{subtitle}</span>
                </div>
              )}
            </>
          )}
        </div>
        <div className="ml-auto flex flex-shrink-0 items-center gap-1">
          {(entity as WikiSuggestion)?.type === "wiki" && (
            <div className="flex items-center space-x-0 rounded-full border p-1 text-eoe-gray dark:text-eoe-dark-text">
              <img
                src="https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg"
                alt="Wikipedia"
                className="h-5 w-5 rounded-full"
              />
              <Plus className="h-5 w-5 rounded-full" />
            </div>
          )}
          {showCloseChip && (
            <button
              type="button"
              aria-label="Remove"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEntityChipClose?.();
              }}
              className="mr-2 flex h-5 w-5 flex-shrink-0 items-center justify-center text-eoe-text-sub dark:text-eoe-dark-text-sub"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (entityChip && showLink && !compactCard) {
    return (
      <div
        className={`entity-chip-showlink group flex w-full min-w-0 flex-row items-center gap-1 overflow-hidden rounded-full py-0 pl-0 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className} bg-transparent dark:bg-transparent`}
      >
        <Link to={link} className="text-decoration-none hover:no-underline" onClick={(e) => e.stopPropagation()}>
          <div
            className={`${entityAvatarSizeClass.thumb} flex-shrink-0 overflow-hidden rounded-full bg-transparent dark:bg-transparent ${ringClass}`}
          >
            {thumbnail ? (
              <img alt={title} src={thumbnail} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-gray">
                {title.charAt(0).toUpperCase()}
                {subtitle && (
                  <>
                    {separator}
                    {subtitle?.charAt(0).toUpperCase()}
                  </>
                )}
              </div>
            )}
          </div>
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-start gap-x-1">
          <Link
            to={link}
            className="text-decoration-none hover:no-underline"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="group flex min-w-0 flex-row items-center justify-between gap-1">
              {entityType === "team" && (
                <span className="flex-shrink-0 leading-tight">
                  <Users className={`size-3.5 ${userEntityDetails ? "text-green-600" : ""}`} />
                </span>
              )}
              {userEntityDetails?.isBestea && (
                <span className="flex-shrink-0 leading-tight">
                  <HeartHandshake className="size-3.5 text-green-600" />
                </span>
              )}
              <span className="line-clamp-1 break-all text-left text-sm font-semibold leading-tight text-decoration-none hover:underline">
                {title}
              </span>
              {badgeText && (
                <span
                  className={`badge-text flex min-w-6 flex-shrink-0 items-center justify-center rounded-full px-1 text-xs text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""}`}
                >
                  {badgeText}
                </span>
              )}
              {badgeElement && <div className="badge-element flex-shrink-0 text-sm leading-tight">{badgeElement}</div>}
            </div>
          </Link>
          {(subtitle || Boolean((entity as { is_comment?: boolean })?.is_comment)) && (
            <Link
              to={subLink || link}
              className="line-clamp-1 break-all text-left text-decoration-none hover:no-underline"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="flex min-w-0 flex-row items-center gap-1">
                {crosspostData && (
                  <CrosspostIcon className="size-3 flex-shrink-0 fill-eoe-text-sub stroke-eoe-text-sub dark:fill-eoe-dark-text-sub dark:stroke-eoe-dark-text-sub" />
                )}
                {(entity as { is_comment?: boolean })?.is_comment && entityType !== "issue" && (
                  <span
                    className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-xxs text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""}`}
                  >
                    {parentEntityType}
                  </span>
                )}
                {subtitle && (
                  <>
                    {(entity as unknown as Issue)?.properties?.type === "Compound" && entityType === "issue" ? (
                      <div className="flex min-w-0 flex-shrink flex-row items-center gap-x-0.5">
                        <InfluencedIcon className="size-3.5 flex-shrink-0 rotate-180" />{" "}
                        <span className="line-clamp-1 flex-shrink break-all text-sm font-semibold">{subtitle}</span>
                      </div>
                    ) : (
                      <div className="flex min-w-0 flex-shrink flex-row items-center gap-x-1">
                        <span className="line-clamp-1 break-all text-xs">{subtitle}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Link>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {(entity as WikiSuggestion)?.type === "wiki" && (
            <div className="flex items-center space-x-0 rounded-full border p-1 text-eoe-gray dark:text-eoe-dark-text">
              <img
                src="https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg"
                alt="Wikipedia"
                className="h-5 w-5 rounded-full"
              />
              <Plus className="h-5 w-5 rounded-full" />
            </div>
          )}
          {showCloseChip && (
            <button
              type="button"
              aria-label="Remove"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEntityChipClose?.();
              }}
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-eoe-text-sub text-eoe-text-sub dark:border-eoe-dark-text-sub dark:text-eoe-dark-text-sub"
            >
              <X className="" />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (entityChip && showLink && compactCard) {
    return (
      <div
        className={`entity-chip-compact group flex w-full min-w-0 flex-col items-start gap-1 rounded-full py-1 pl-1 pr-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}
      >
        <div className="flex flex-row items-center gap-1">
          <Link to={link} className="text-decoration-none hover:no-underline" onClick={(e) => e.stopPropagation()}>
            <div
              className={`${entityAvatarSizeClass.thumb} flex-shrink-0 overflow-hidden rounded-full bg-transparent dark:bg-transparent ${ringClass}`}
            >
              {thumbnail ? (
                <img alt={title} src={thumbnail} className="w-full" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-gray">
                  {title.charAt(0).toUpperCase()}
                  {subtitle && (
                    <>
                      {separator}
                      {subtitle?.charAt(0).toUpperCase()}
                    </>
                  )}
                </div>
              )}
            </div>
          </Link>
          <div className="flex min-w-0 flex-grow flex-col justify-start">
            <Link
              to={link}
              className="text-decoration-none hover:no-underline"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="group flex min-w-0 flex-row items-center gap-1">
                {entityType === "team" && (
                  <span className="flex-shrink-0 leading-tight">
                    <Users className={`size-3.5 ${userEntityDetails ? "text-green-600" : ""}`} />
                  </span>
                )}
                {userEntityDetails?.isBestea && (
                  <span className="flex-shrink-0 leading-tight">
                    <HeartHandshake className="size-3.5 text-green-600" />
                  </span>
                )}
                <span className="line-clamp-1 break-all font-semibold leading-snug group-hover:underline hover:underline">
                  {title}
                </span>
                {badgeText && (
                  <span
                    className={`flex flex-shrink-0 whitespace-nowrap rounded-full px-1 text-xs text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""}`}
                  >
                    {badgeText}
                  </span>
                )}
                {badgeElement && <div className="flex-shrink-0 text-sm leading-tight">{badgeElement}</div>}
              </div>
            </Link>
            {username && (
              <div className="card-username group flex w-fit items-center">
                {actorAvatarNode ? <span className="mr-1 flex-shrink-0">{actorAvatarNode}</span> : null}
                <TooltipWrapper title={`View ${username}'s profile`} arrow>
                  <Link
                    to={`/user/${username}`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="card-username line-clamp-1 break-all"
                  >
                    <span className="entity-avatar-overhang-safe line-clamp-1 break-all text-sm font-medium">{username}</span>
                  </Link>
                </TooltipWrapper>
                {renderDateTooltip("flex flex-row items-center gap-0 text-sm font-medium")}
                {badgeText && (
                  <span
                    className={`flex flex-shrink-0 whitespace-nowrap rounded-full px-1 text-xs text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""}`}
                  >
                    {badgeText}
                  </span>
                )}
                {badgeElement && <div className="flex-shrink-0 text-sm leading-tight">{badgeElement}</div>}
              </div>
            )}
          </div>
        </div>
        <div className="flex min-w-0 flex-col justify-start gap-1">
          {(subtitle || Boolean((entity as { is_comment?: boolean })?.is_comment)) && (
            <Link
              to={subLink || link}
              className="text-decoration-none hover:no-underline"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="flex min-w-0 flex-shrink flex-row items-center gap-1">
                {crosspostData && (
                  <CrosspostIcon className="size-3 flex-shrink-0 fill-eoe-text-sub stroke-eoe-text-sub dark:fill-eoe-dark-text-sub dark:stroke-eoe-dark-text-sub" />
                )}
                {(entity as { is_comment?: boolean })?.is_comment && entityType !== "issue" && (
                  <span
                    className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-xxs text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""}`}
                  >
                    {parentEntityType}
                  </span>
                )}
                <span className="text-base leading-snug">{subtitle}</span>
              </div>
            </Link>
          )}
        </div>
        <div className="ml-auto flex flex-shrink-0 items-center gap-1">
          {(entity as WikiSuggestion)?.type === "wiki" && (
            <div className="flex items-center space-x-0 rounded-full border p-1 text-eoe-gray dark:text-eoe-dark-text">
              <img
                src="https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg"
                alt="Wikipedia"
                className="h-5 w-5 rounded-full"
              />
              <Plus className="h-5 w-5 rounded-full" />
            </div>
          )}
          {showCloseChip && (
            <button
              type="button"
              aria-label="Remove"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEntityChipClose?.();
              }}
              className="mr-2 flex h-5 w-5 flex-shrink-0 items-center justify-center text-eoe-text-sub dark:text-eoe-dark-text-sub"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`entity-avatar-default flex min-w-0 items-center gap-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}
    >
      <Link
        to={link}
        onClick={(e) => e.stopPropagation()}
        className={`flex items-center gap-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}
      >
        <div
          className={`${entityAvatarSizeClass.thumb} flex-shrink-0 overflow-hidden rounded-full bg-transparent dark:bg-transparent ${ringClass}`}
        >
          {thumbnail ? (
            <img alt={title} src={thumbnail} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-gray">
              {title.charAt(0).toUpperCase()}
              {subtitle && (
                <>
                  {separator}
                  {subtitle?.charAt(0).toUpperCase()}
                </>
              )}
            </div>
          )}
        </div>
      </Link>
      <div className="flex min-w-0 flex-col">
        {!isUserCenteredAvatar && (
          <Link to={link} onClick={(e) => e.stopPropagation()} className="group">
            <div className="group flex min-w-0 flex-row items-center gap-1">
              <span className="line-clamp-1 min-w-0 break-all font-semibold group-hover:underline hover:underline">
                {title}
                {subtitle && (
                  <>
                    <span>{separator ?? " "}</span>
                    <span>{subtitle}</span>
                  </>
                )}
              </span>
              <span
                className={`flex-shrink-0 whitespace-nowrap rounded-full px-1.5 text-xs font-medium text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""}`}
              >
                {parentEntityType}
              </span>
            </div>
          </Link>
        )}
        {username && (
          <div className="card-username flex items-center">
            {actorAvatarNode ? <span className="mr-1 flex-shrink-0">{actorAvatarNode}</span> : null}
            <TooltipWrapper title={`View ${username}'s profile`} arrow>
              <Link
                to={`/user/${username}`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="card-username line-clamp-1 break-all"
              >
                <span className="entity-avatar-overhang-safe text-sm font-medium line-clamp-1 break-all">{username}</span>
              </Link>
            </TooltipWrapper>
            {renderDateTooltip("card-date flex flex-shrink-0 flex-row items-center gap-0 text-sm font-medium")}
            {badgeText && (
              <span
                className={`flex flex-shrink-0 whitespace-nowrap rounded-full px-1 text-xs text-white no-underline hover:no-underline group-hover:no-underline ${entityAvatarColorMap[colorKey] ?? ""}`}
              >
                {badgeText}
              </span>
            )}
            {badgeElement && <div className="flex-shrink-0 text-sm leading-tight">{badgeElement}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
